import { supabase } from './supabase';

// ── CRUD ────────────────────────────────────────────────────────────────────

export async function getShows(userId) {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from('shows')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error) { console.error('getShows error:', error); return []; }
  return data || [];
}

export async function createShow(userId, show) {
  if (!supabase || !userId) throw new Error('Not authenticated');
  const row = {
    user_id: userId,
    name: show.name || 'Untitled Show',
    description: show.description || '',
    target_audience: show.target_audience || '',
    brand_color: show.brand_color || '#c6364a',
    cover_art_url: show.cover_art_url || '',
  };
  const { data, error } = await supabase
    .from('shows')
    .insert(row)
    .select()
    .single();
  if (error) { console.error('createShow error:', error); throw error; }
  return data;
}

export async function updateShow(showId, userId, updates) {
  if (!supabase || !userId || !showId) throw new Error('Missing ids');
  const { data, error } = await supabase
    .from('shows')
    .update(updates)
    .eq('id', showId)
    .eq('user_id', userId)
    .select();
  if (error) { console.error('updateShow error:', error); throw error; }
  if (!data || data.length === 0) {
    // RLS silently blocked the UPDATE (0 rows affected). Surface it loudly.
    throw new Error('Update blocked by database permissions. Run the latest migration (005) in Supabase SQL Editor.');
  }
  return data[0];
}

export async function deleteShow(showId, userId) {
  if (!supabase || !userId || !showId) throw new Error('Missing ids');
  // `.select()` forces Supabase to return the deleted rows so we can verify
  // the delete actually happened. Without it, RLS-blocked deletes silently
  // return `data: null, error: null` — the show looks gone until the next
  // refetch brings it back.
  const { data, error } = await supabase
    .from('shows')
    .delete()
    .eq('id', showId)
    .eq('user_id', userId)
    .select();
  if (error) { console.error('deleteShow error:', error); throw error; }
  if (!data || data.length === 0) {
    throw new Error('Delete blocked by database permissions. Run the latest migration (005) in Supabase SQL Editor.');
  }
  return data[0];
}

// ── Episode-count aggregation ──────────────────────────────────────────────
// Computes episode counts per show from the episodes table. Returns a map
// keyed by show_id with { count, last_activity } values. Stays in sync even
// if the shows.episode_count column drifts.
export async function getShowEpisodeStats(userId) {
  if (!supabase || !userId) return {};
  const { data, error } = await supabase
    .from('episodes')
    .select('show_id, created_at')
    .eq('user_id', userId)
    .not('show_id', 'is', null);
  if (error) { console.error('getShowEpisodeStats error:', error); return {}; }
  const stats = {};
  for (const ep of data || []) {
    const k = ep.show_id;
    if (!stats[k]) stats[k] = { count: 0, last_activity: null };
    stats[k].count += 1;
    if (!stats[k].last_activity || ep.created_at > stats[k].last_activity) {
      stats[k].last_activity = ep.created_at;
    }
  }
  return stats;
}

// ── Default show bootstrap + one-time legacy episode backfill ───────────────
// On first successful call for a user who has zero shows, creates "My Show"
// (using their existing podcaster_profiles.show_name if present) and links
// all of their existing episodes to it. Guarded by a localStorage flag so it
// only ever runs once per user per device.
export async function ensureDefaultShow(userId, profile) {
  if (!supabase || !userId) return null;

  const flagKey = `shows_migrated_${userId}`;
  const alreadyRan = typeof window !== 'undefined' && window.localStorage.getItem(flagKey) === '1';

  // Fast path: already migrated, just return the first show if any
  if (alreadyRan) {
    const shows = await getShows(userId);
    return shows[0] || null;
  }

  const existing = await getShows(userId);
  if (existing.length > 0) {
    // User already has shows but flag wasn't set — still backfill any orphan episodes
    await backfillLegacyEpisodes(userId, existing[0].id);
    if (typeof window !== 'undefined') window.localStorage.setItem(flagKey, '1');
    return existing[0];
  }

  // Create a default show, seeded with their profile's show_name if they have one
  const defaultName = profile?.show_name?.trim() || 'My Show';
  const defaultShow = await createShow(userId, {
    name: defaultName,
    description: profile?.niche ? `${profile.niche} podcast` : '',
  });

  // Link all their existing episodes to the new default show
  await backfillLegacyEpisodes(userId, defaultShow.id);

  if (typeof window !== 'undefined') window.localStorage.setItem(flagKey, '1');
  return defaultShow;
}

async function backfillLegacyEpisodes(userId, showId) {
  if (!supabase || !userId || !showId) return;
  const { error } = await supabase
    .from('episodes')
    .update({ show_id: showId })
    .eq('user_id', userId)
    .is('show_id', null);
  if (error) console.error('backfillLegacyEpisodes error:', error);
}
